import React, { useState, useCallback, useMemo } from "react";
import { Box, Checkbox, Chip, CircularProgress } from "@material-ui/core";
import { IGameShort } from "../../../../interfaces";
import { useGetPackageGamesQuery } from "../../../../api/games";
import { useStyles } from "../styles";

interface GameListProps {
    packageId: number;
    selectedGames: number[];
    isPackageOpen: boolean;
    isCustomMode: boolean;
    onToggleGame: (gameId: number) => void;
}

export const GameList: React.FC<GameListProps> = React.memo(({
    packageId,
    selectedGames,
    isPackageOpen,
    isCustomMode,
    onToggleGame
}) => {
    const classes = useStyles();
    const [page, setPage] = useState(1);

    const shouldFetch = useMemo(() => isPackageOpen && isCustomMode, [isPackageOpen, isCustomMode]);

    const { data, isFetching } = useGetPackageGamesQuery(
        { packageId, page, perPage: 50 },
        { skip: !shouldFetch }
    );

    const hasMore = useMemo(() => data?.meta ? page < data.meta.pageCount : false, [data?.meta, page]);

    const handleGamesScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const node = event.currentTarget;
        const isBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 20;

        if (isBottom && hasMore && !isFetching) {
            setPage(prev => prev + 1);
        }
    }, [hasMore, isFetching]);

    const games = useMemo(() => data?.items || [], [data?.items]);

    return (
        <Box
            className={classes.chips}
            mt={1}
            onScroll={handleGamesScroll}
            style={{ maxHeight: 250, overflowY: "auto", padding: "4px 0" }}
        >
            {games.map((game: IGameShort) => (
                <Chip
                    className={classes.chip}
                    key={game.id}
                    onClick={() => onToggleGame(game.id)}
                    label={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <span>{game.name}</span>
                            <Checkbox
                                checked={selectedGames.includes(game.id)}
                                color="default"
                            />
                        </Box>
                    }
                />
            ))}

            {isFetching && (
                <Box width="100%" display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    );
});